import WalletButton from 'features/cardManagement/components/WalletButton/WalletButton';
import { WalletButtonProps } from 'features/cardManagement/components/WalletButton/WalletButtonShared';
import React from 'react';
import { Platform, ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button, { ButtonProps } from 'shared/components/vxl/Button/Button';
import ProductOfferCard, {
  ProductOfferCardProps,
} from 'shared/components/vxl/cards/ProductOfferCard/ProductOfferCard';
import Illustration, {
  IllustrationKeyType,
} from 'shared/components/vxl/Illustration/Illustration';
import ExpressiveXLText from 'shared/components/vxl/typography/expressive/ExpressiveXLText';
import VaroHandMdText from 'shared/components/vxl/typography/expressive/varoHand/VaroHandMdText';
import { isWeb } from 'shared/constants/platform';
import {
  AnalyticsProps,
  useViewDidFocusScreenAnalyticsEvent,
} from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import theme from 'shared/styles/theme';

export interface SuccessScreenProps extends Omit<AnalyticsProps, 'key'> {
  primaryButton: Pick<
    ButtonProps,
    | 'label'
    | 'isDisabled'
    | 'onPress'
    | 'isLoading'
    | 'analyticsId'
    | 'customAttributes'
    | 'componentAnalytics'
  >;
  titleText: string;
  visual: IllustrationKeyType;
  content?: React.ReactNode;
  eyebrowText?: string;
  screenAnalyticsEventKey?: string | undefined;
  secondaryButton?: Pick<
    ButtonProps,
    | 'label'
    | 'isDisabled'
    | 'onPress'
    | 'isLoading'
    | 'analyticsId'
    | 'customAttributes'
  >;
  addWalletButton?: WalletButtonProps;
  productOfferCard?: ProductOfferCardProps;
}

const SuccessScreen = ({
  visual,
  titleText,
  eyebrowText,
  content,
  primaryButton,
  secondaryButton,
  addWalletButton,
  screenAnalyticsEventKey,
  customAttributes = null,
  productOfferCard,
}: SuccessScreenProps) => {
  useViewDidFocusScreenAnalyticsEvent(
    screenAnalyticsEventKey
      ? {
          key: screenAnalyticsEventKey,
          customAttributes,
        }
      : null,
  );

  const insets = useSafeAreaInsets();

  return (
    <View
      style={[
        styles.container,
        {
          paddingBottom: insets.bottom ? insets.bottom : theme.spacing.xl,
          paddingTop: insets.top,
        },
      ]}
    >
      <ScrollView contentContainerStyle={styles.scrollView}>
        <View style={styles.illustrationContainer}>
          <View style={styles.illustrationSizeContainer}>
            <Illustration illustration={visual} />
          </View>
        </View>
        {eyebrowText ? (
          <VaroHandMdText style={styles.eyeBrowText} numberOfLines={1}>
            {eyebrowText}
          </VaroHandMdText>
        ) : null}
        {/* TODO JT-132547: remove this spacing modification once we migrate to latest RN version */}
        <View style={styles.titleSpacing}>
          <ExpressiveXLText style={styles.titleText}>
            {titleText}
          </ExpressiveXLText>
        </View>
        {content ? (
          <View style={styles.contentContainer}>{content}</View>
        ) : null}
        {productOfferCard ? (
          <ProductOfferCard hasBorder {...productOfferCard} />
        ) : null}
      </ScrollView>

      {addWalletButton && !isWeb ? (
        <View style={styles.buttonContainer}>
          <WalletButton {...addWalletButton} style={styles.walletButton} />
          {primaryButton ? (
            // show primary button with secondary style
            <Button
              label={primaryButton.label}
              onPress={primaryButton.onPress}
              size="LARGE"
              type="SECONDARY"
              width="FULL"
              analyticsId={primaryButton.analyticsId}
              customAttributes={primaryButton.customAttributes}
              componentAnalytics={primaryButton.componentAnalytics}
              isLoading={primaryButton.isLoading}
            />
          ) : null}
          {!primaryButton && secondaryButton ? (
            // no primary then use secondary
            <Button
              label={secondaryButton.label}
              onPress={secondaryButton.onPress}
              size="LARGE"
              type="SECONDARY"
              width="FULL"
              analyticsId={secondaryButton.analyticsId}
              customAttributes={secondaryButton.customAttributes}
              isLoading={secondaryButton.isLoading}
            />
          ) : null}
        </View>
      ) : (
        <View style={styles.buttonContainer}>
          <Button
            label={primaryButton.label}
            onPress={primaryButton.onPress}
            size="LARGE"
            type="PRIMARY"
            width="FULL"
            analyticsId={primaryButton.analyticsId}
            customAttributes={primaryButton.customAttributes}
            componentAnalytics={primaryButton.componentAnalytics}
            isLoading={primaryButton.isLoading}
          />
          {secondaryButton ? (
            <Button
              label={secondaryButton.label}
              onPress={secondaryButton.onPress}
              size="LARGE"
              type="SECONDARY"
              width="FULL"
              analyticsId={secondaryButton.analyticsId}
              customAttributes={secondaryButton.customAttributes}
              isLoading={secondaryButton.isLoading}
            />
          ) : null}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  buttonContainer: {
    borderColor: theme.color.border.primary,
    borderTopWidth: 1,
    gap: theme.spacing.m,
    padding: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  contentContainer: {
    paddingVertical: theme.spacing.m,
  },
  eyeBrowText: {
    color: theme.color.interactive.primary,
    marginBottom: theme.spacing.s,
  },
  illustrationContainer: {
    marginBottom: theme.spacing.xl,
  },
  illustrationSizeContainer: {
    height: 208,
    width: 208,
  },
  scrollView: {
    alignItems: 'center',
    flexGrow: 1,
    justifyContent: 'center',
    padding: theme.spacing.m,
  },
  // TODO JT-132547: remove this spacing modification once we migrate to new RN version
  titleSpacing: {
    ...Platform.select({
      native: {
        marginVertical: -9,
      },
    }),
  },
  titleText: {
    textAlign: 'center',
  },
  ...Platform.select({
    android: {
      walletButton: {
        height: 50,
        width: '100%',
      },
    },
    ios: {
      walletButton: {
        height: 40,
        width: '100%',
      },
    },
  }),
});

export default SuccessScreen;
